import type { HistoryPoint } from './history';
import type { WeatherState } from './types';

/**
 * Toată logica de prezentare din design, portată ca funcție pură: primește
 * starea brută + momentul curent și întoarce exclusiv șiruri gata de afișat.
 * Componentele nu fac calcule, iar funcția e testabilă fără DOM.
 */

const MONTHS = [
  'ianuarie', 'februarie', 'martie', 'aprilie', 'mai', 'iunie',
  'iulie', 'august', 'septembrie', 'octombrie', 'noiembrie', 'decembrie',
];

const DAYS = ['duminică', 'luni', 'marți', 'miercuri', 'joi', 'vineri', 'sâmbătă'];

const DIRS = [
  'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
  'S', 'SSV', 'SV', 'VSV', 'V', 'VNV', 'NV', 'NNV',
] as const;

type Direction = (typeof DIRS)[number];

const DIR_NAMES: Record<Direction, string> = {
  N: 'nord', NNE: 'nord-nord-est', NE: 'nord-est', ENE: 'est-nord-est',
  E: 'est', ESE: 'est-sud-est', SE: 'sud-est', SSE: 'sud-sud-est',
  S: 'sud', SSV: 'sud-sud-vest', SV: 'sud-vest', VSV: 'vest-sud-vest',
  V: 'vest', VNV: 'vest-nord-vest', NV: 'nord-vest', NNV: 'nord-nord-vest',
};

/**
 * Stările din setul Home Assistant, pe care le folosește și zigbee2mqtt.
 * Lista e completă intenționat: ce lipsește de aici ajunge pe ecran în engleză,
 * fiindcă `txt` cade pe valoarea brută când nu găsește cheia.
 */
const CONDITIONS: Record<string, string> = {
  sunny: 'Senin', clear: 'Senin', 'clear-night': 'Senin',
  partlycloudy: 'Parțial noros', cloudy: 'Noros', fog: 'Ceață',
  rainy: 'Ploaie', pouring: 'Ploaie torențială', hail: 'Grindină',
  snowy: 'Ninsoare', 'snowy-rainy': 'Lapoviță',
  lightning: 'Descărcări electrice', 'lightning-rainy': 'Furtună cu ploaie',
  windy: 'Vânt', 'windy-variant': 'Vânt puternic',
  exceptional: 'Condiții extreme',
};

const RAIN_STATUS: Record<string, string> = {
  none: 'Uscat', raining: 'Plouă', stopped: 'Ploaie oprită', dry: 'Uscat',
};

const TRENDS: Record<string, string> = {
  rising: 'În creștere', falling: 'În scădere', steady: 'Staționară', stable: 'Staționară',
};

const HEAT_STRESS: Record<string, string> = {
  comfortable: 'Confortabil', caution: 'Atenție', warm: 'Cald', hot: 'Caniculă',
  cold: 'Frig', 'extreme caution': 'Atenție sporită', danger: 'Periculos',
};

const BEAUFORT = [
  'Calm', 'Adiere', 'Briză ușoară', 'Briză slabă', 'Briză moderată',
  'Briză destul de tare', 'Briză tare', 'Vânt puternic', 'Furtună moderată',
  'Furtună', 'Furtună puternică', 'Furtună violentă', 'Uragan',
];

/** Pragurile inferioare, în km/h, pentru gradele Beaufort 1–12. */
const BEAUFORT_THRESHOLDS = [1, 6, 12, 20, 29, 39, 50, 62, 75, 89, 103, 117];

/** Vânt afișat pe bară: 0–60 km/h, ca în design. */
const WIND_SCALE_MAX = 60;
/** Presiune pe scala 980–1040 hPa. */
const PRESSURE_MIN = 980;
const PRESSURE_SPAN = 60;
/** Peste 5 minute fără mesaj considerăm senzorul „fără date recente”. */
const STALE_AFTER_SECONDS = 300;
/** Variație de presiune, în hPa, sub care trendul se citește ca staționar. */
const PRESSURE_TREND_DEADBAND = 0.3;
/** Același prag, când senzorul raportează tendința ca rată, în hPa/h. */
const PRESSURE_RATE_DEADBAND = 0.3;
/** Câte puncte în urmă privim pentru trend, când backendul nu îl trimite. */
const PRESSURE_TREND_LOOKBACK = 3;
/** Aproximare uzuală lux → W/m² pentru lumină solară. */
const LUX_PER_WATT = 126.7;

const EM_DASH = '—';

function has(value: unknown): value is number | string {
  return value !== null && value !== undefined && value !== '';
}

function toNumber(value: unknown): number | null {
  if (!has(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Număr cu virgulă zecimală (RO) și unitate opțională. */
function num(value: unknown, decimals = 1, unit?: string): string {
  const parsed = toNumber(value);
  if (parsed === null) return EM_DASH;
  return parsed.toFixed(decimals).replace('.', ',') + (unit ? ` ${unit}` : '');
}

function txt(value: unknown, map?: Record<string, string>): string {
  if (!has(value)) return EM_DASH;
  const key = String(value).toLowerCase();
  return map?.[key] ?? String(value);
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function clamp(value: number, min = 0, max = 100): number {
  return Math.max(min, Math.min(max, value));
}

function percent(value: number | null, max: number): string {
  return `${value === null ? 0 : clamp((value / max) * 100)}%`;
}

/** `stroke-dasharray` pentru un inel completat proporțional. */
function arc(radius: number, ratio: number): string {
  const circumference = 2 * Math.PI * radius;
  return `${(circumference * clamp(ratio, 0, 1)).toFixed(1)} ${circumference.toFixed(1)}`;
}

/**
 * Trendul presiunii: îl preferăm pe cel calculat de backend, iar dacă lipsește
 * îl deducem din istoric comparând ultima valoare cu cea de acum câteva puncte.
 */
function pressureTrend(
  reported: unknown,
  history: HistoryPoint[],
): { label: string; arrow: string } {
  if (has(reported)) {
    // Stația poate raporta tendința ca rată de variație, în hPa/h. O afișăm ca
    // atare — săgeata dă sensul, cifra rămâne cea măsurată.
    const rate = toNumber(reported);
    if (rate !== null) {
      const arrow =
        rate > PRESSURE_RATE_DEADBAND ? '↑' : rate < -PRESSURE_RATE_DEADBAND ? '↓' : '→';
      return { label: num(Math.abs(rate), 1, 'hPa/h'), arrow };
    }

    const key = String(reported).toLowerCase();
    const label = TRENDS[key];
    if (label !== undefined) {
      const arrow = key === 'rising' ? '↑' : key === 'falling' ? '↓' : '→';
      return { label, arrow };
    }
  }

  const series = history
    .map((point) => toNumber(point.pressure))
    .filter((value): value is number => value !== null);

  if (series.length <= PRESSURE_TREND_LOOKBACK) {
    return { label: 'Fără istoric', arrow: '·' };
  }

  const delta = series[series.length - 1] - series[series.length - 1 - PRESSURE_TREND_LOOKBACK];
  if (delta > PRESSURE_TREND_DEADBAND) return { label: 'În creștere', arrow: '↑' };
  if (delta < -PRESSURE_TREND_DEADBAND) return { label: 'În scădere', arrow: '↓' };
  return { label: 'Staționară', arrow: '→' };
}

function relativeAge(seconds: number): string {
  if (seconds < 60) return `acum ${seconds} s`;
  if (seconds < 3600) return `acum ${Math.round(seconds / 60)} min`;
  if (seconds < 86_400) return `acum ${Math.round(seconds / 3600)} h`;
  return `acum ${Math.round(seconds / 86_400)} zile`;
}

export interface WeatherView {
  stationName: string;
  condition: string;
  /**
   * `false` când nu se știe cum e cerul — noaptea, sau când stației îi lipsesc
   * coordonatele. Interfața ascunde eticheta, în loc să arate o liniuță.
   */
  hasCondition: boolean;
  clock: string;
  dateLabel: string;
  lastSeenTime: string;
  lastSeenAgo: string;
  statusNote: string;
  /** `true` când senzorul n-a mai raportat de peste 5 minute. */
  stale: boolean;

  temperature: string;
  apparentTemperature: string;
  dewPoint: string;
  humidex: string;
  heatStress: string;
  windChill: string;
  humidity: string;
  humArc: string;

  windSpeed: string;
  windDirShort: string;
  windWidth: string;
  gustSpeed: string;
  windDirectionLabel: string;
  /** Unghiul acului busolei, în grade. */
  windAngle: number;
  beaufort: string;

  precipitation: string;
  rainRate: string;
  rainStatus: string;

  pressureValue: string;
  pressurePosition: string;
  pressureTrend: string;
  pressureArrow: string;

  uvIndex: string;
  uvWidth: string;
  uvLabel: string;
  uvAdvice: string;

  illuminance: string;
  solarRad: string;
  daylight: string;

  battery: string;
  batteryWidth: string;
  linkquality: string;
  linkWidth: string;
  capacitorVoltage: string;
  voltage: string;
}

export function buildWeatherView(
  state: WeatherState | null,
  now: Date,
  stationName: string,
  history: HistoryPoint[] = [],
): WeatherView {
  const d = state ?? {};

  const windSpeed = toNumber(d.wind_speed);
  const windDirection = toNumber(d.wind_direction);
  const humidity = toNumber(d.humidity);
  const uv = toNumber(d.uv_index);
  const lux = toNumber(d.illuminance);
  const link = toNumber(d.linkquality);
  const pressure = toNumber(d.pressure);
  const battery = toNumber(d.battery);

  let beaufort: number | null = null;
  if (windSpeed !== null) {
    beaufort = 0;
    while (beaufort < 12 && windSpeed >= BEAUFORT_THRESHOLDS[beaufort]) beaufort++;
  }

  const dirKey = windDirection === null ? null : DIRS[Math.round(windDirection / 22.5) % 16];

  const seen = has(d.last_seen) ? new Date(d.last_seen as string) : null;
  const seenValid = seen !== null && !Number.isNaN(seen.getTime());
  let lastSeenAgo = EM_DASH;
  let stale = false;
  if (seenValid) {
    const seconds = Math.max(0, Math.round((now.getTime() - seen.getTime()) / 1000));
    stale = seconds > STALE_AFTER_SECONDS;
    lastSeenAgo = relativeAge(seconds);
  }

  const linkPercent = link === null ? null : Math.round(link / 2.55);
  const trend = pressureTrend(d.pressure_trend, history);

  return {
    stationName,
    condition: txt(d.weather_condition, CONDITIONS),
    hasCondition: has(d.weather_condition),
    clock: `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`,
    dateLabel: `${DAYS[now.getDay()]}, ${now.getDate()} ${MONTHS[now.getMonth()]} ${now.getFullYear()}`,
    lastSeenTime: seenValid
      ? `${pad(seen.getHours())}:${pad(seen.getMinutes())}:${pad(seen.getSeconds())}`
      : EM_DASH,
    lastSeenAgo,
    statusNote: stale ? 'Fără date recente de la senzor' : 'Recepție normală',
    stale,

    temperature: has(d.temperature) ? num(d.temperature, 1) : '– –',
    apparentTemperature: num(d.apparent_temperature, 1, '°C'),
    dewPoint: num(d.dew_point, 1, '°C'),
    humidex: num(d.humidex, 1, '°C'),
    // Stația raportează confortul termic fie ca etichetă, fie ca indice în
    // procente; îl arătăm în forma în care vine.
    heatStress:
      toNumber(d.heat_stress) !== null
        ? num(d.heat_stress, 0, '%')
        : txt(d.heat_stress, HEAT_STRESS),
    windChill: num(d.wind_chill, 1, '°C'),
    humidity: num(d.humidity, 0, '%'),
    humArc: arc(50, humidity === null ? 0 : humidity / 100),

    windSpeed: windSpeed === null ? '–' : String(Math.round(windSpeed)),
    windDirShort: dirKey ?? EM_DASH,
    windWidth: percent(windSpeed, WIND_SCALE_MAX),
    gustSpeed: num(d.gust_speed, 0, 'km/h'),
    windDirectionLabel:
      dirKey === null || windDirection === null
        ? 'Direcție necunoscută'
        : `Din ${DIR_NAMES[dirKey]} · ${Math.round(windDirection)}°`,
    windAngle: windDirection ?? 0,
    // Doar descrierea: cuvântul spune ceva oricui, cifra doar cui știe scara.
    beaufort: beaufort === null ? EM_DASH : BEAUFORT[beaufort],

    precipitation: has(d.precipitation) ? num(d.precipitation, 1, 'mm') : EM_DASH,
    rainRate: num(d.rain_rate, 1, 'mm/h'),
    rainStatus: txt(d.rain_status, RAIN_STATUS),

    pressureValue: pressure === null ? EM_DASH : num(pressure, 1),
    pressurePosition:
      pressure === null ? '0%' : `${clamp(((pressure - PRESSURE_MIN) / PRESSURE_SPAN) * 100)}%`,
    pressureTrend: trend.label,
    pressureArrow: trend.arrow,

    uvIndex: num(d.uv_index, 1),
    uvWidth: percent(uv, 11),
    uvLabel:
      uv === null ? 'Indisponibil'
      : uv < 3 ? 'Scăzut'
      : uv < 6 ? 'Moderat'
      : uv < 8 ? 'Ridicat'
      : uv < 11 ? 'Foarte ridicat'
      : 'Extrem',
    uvAdvice:
      uv === null ? 'Senzorul nu a raportat'
      : uv < 3 ? 'Fără măsuri de protecție'
      : uv < 6 ? 'Protecție la expunere lungă'
      : 'Evită soarele între 12:00 și 16:00',

    illuminance:
      lux === null ? EM_DASH : lux >= 1000 ? num(lux / 1000, 1, 'klux') : num(lux, 0, 'lux'),
    solarRad: lux === null ? EM_DASH : num(lux / LUX_PER_WATT, 0, 'W/m²'),
    daylight:
      lux === null ? EM_DASH
      : lux < 10 ? 'Întuneric'
      : lux < 1000 ? 'Lumină slabă'
      : lux < 20_000 ? 'Cer acoperit'
      : 'Soare direct',

    battery: num(d.battery, 0, '%'),
    batteryWidth: battery === null ? '0%' : `${clamp(battery)}%`,
    linkquality: linkPercent === null ? EM_DASH : `${linkPercent} %`,
    linkWidth: linkPercent === null ? '0%' : `${clamp(linkPercent)}%`,
    capacitorVoltage: num(d.capacitor_voltage, 2, 'V'),
    voltage: num(d.voltage, 2, 'V'),
  };
}
